import { getItemUid } from 'entities/CatalogItem/lib/getItemUid';
import { catalogItemActions } from 'entities/CatalogItem/model/slices/CatalogItemSlice';
import { getCustomListCatalogItem } from 'entities/CustomList';
import { fetchOrder } from 'entities/Order';
import {
    getListOrderCoverageFromQuantities,
    listItemTargetQuantity,
} from 'features/AddCustomListToOrder/lib/getListOrderCoverage';
import { editOrderItem } from 'features/editItemsInOrder/model/services/editOrderItem/editOrderItem';
import { deleteOrderItem } from 'features/removeItemsInOrder/model/services/deleteOrderItem/deleteOrderItem';

import type { AppDispatch } from 'app/providers/StoreProvider';
import type { MappedCatalogItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import type { CustomListItem } from 'entities/CustomList';
import type { CatalogIdentity } from 'shared/lib/contexts/CatalogIdentityContext';

interface RemoveCustomListItemsFromOrderOptions {
    dispatch: AppDispatch;
    listItems: CustomListItem[];
    identity: CatalogIdentity;
    selectedItems: Record<string, Partial<MappedCatalogItem>> | undefined;
    orderId?: string;
}

interface ResolvedListOrderLine {
    selectionKey: string;
    orderItemId?: string;
    qty: number;
    targetQuantity: number;
}

const resolveListOrderLines = (
    options: RemoveCustomListItemsFromOrderOptions,
): ResolvedListOrderLine[] =>
    options.listItems.flatMap((listItem) => {
        const catalogItem = getCustomListCatalogItem(listItem);
        if (!catalogItem) {
            return [];
        }

        const selectionKey = getItemUid(catalogItem, options.identity);
        const selection = options.selectedItems?.[selectionKey];

        return [
            {
                selectionKey,
                orderItemId: selection?.orderItemId,
                qty: selection?.qty ?? 0,
                targetQuantity: listItemTargetQuantity(listItem),
            },
        ];
    });

const removeOneListItem = async (
    options: RemoveCustomListItemsFromOrderOptions,
    resolved: ResolvedListOrderLine,
    listMultiple: number,
): Promise<boolean> => {
    const { selectionKey, orderItemId } = resolved;
    const quantityToRemove = listMultiple * resolved.targetQuantity;
    const nextQty = resolved.qty - quantityToRemove;

    if (quantityToRemove < 1) {
        return true;
    }

    if (options.orderId && orderItemId) {
        const result =
            nextQty < 1
                ? await options.dispatch(
                      deleteOrderItem({
                          orderId: options.orderId,
                          itemId: orderItemId,
                      }),
                  )
                : await options.dispatch(
                      editOrderItem({
                          orderId: options.orderId,
                          itemId: orderItemId,
                          qty: nextQty,
                      }),
                  );
        if (result.meta.requestStatus !== 'fulfilled') {
            return false;
        }
    }

    if (nextQty < 1) {
        options.dispatch(
            catalogItemActions.removeMultiselectItem(selectionKey),
        );
        options.dispatch(catalogItemActions.removeNewItem(selectionKey));
        return true;
    }

    options.dispatch(
        catalogItemActions.setItemQuantity({
            id: selectionKey,
            value: nextQty,
        }),
    );
    return true;
};

export const removeCustomListItemsFromOrder = async (
    options: RemoveCustomListItemsFromOrderOptions,
): Promise<boolean> => {
    const resolvedLines = resolveListOrderLines(options);
    const { listMultiple } = getListOrderCoverageFromQuantities(resolvedLines);

    if (listMultiple < 1) {
        return true;
    }

    for (const resolved of resolvedLines) {
        const removed = await removeOneListItem(
            options,
            resolved,
            listMultiple,
        );

        if (!removed) {
            return false;
        }
    }

    if (options.orderId) {
        await options.dispatch(
            fetchOrder({ id: options.orderId, isCatalog: true }),
        );
    }

    return true;
};
